// Legal document content for the public /terms, /privacy and /disclaimer pages.
//
// The text below is authored in Markdown (rendered by src/components/Legal/Markdown.tsx)
// and is the verbatim content of the source files in src/Legal/:
//   - MyCryptoPlanner_Terms_of_Service.md
//   - MyCryptoPlanner_Privacy_Policy.md
//   - MyCryptoPlanner_Disclaimer.md
// It lives here as string constants because CRA does not import .md files as raw
// strings without extra loader config. If the source docs change, update these to
// match. Internal cross-links (e.g. "/terms") are clickable via React Router.

export type LegalDocId = 'terms' | 'privacy' | 'disclaimer';

interface LegalDoc {
  /** Short title used for the browser tab (<title>). */
  title: string;
  /** Markdown source. */
  content: string;
}

const TERMS_MD = `# Terms of Service

**Last updated: 1 September 2026**

Please read these Terms of Service ("**Terms**") carefully before using MyCryptoPlanner. By creating an account or using the service, you agree to be bound by these Terms. If you do not agree, do not use the service.

---

## 1. Who we are

MyCryptoPlanner (the "**Service**", "**we**", "**us**", "**our**") is a website and web application available at mycryptocurrencyplanner.com.

The Service is operated by **Oliver Joseph Lazare**, a sole trader trading as **My Crypto Planner** (**ABN 46 604 154 915**), based in Western Australia, Australia.

You can contact us at any time at **mycryptoplanner@outlook.com**.

---

## 2. What the Service is (and what it is NOT)

MyCryptoPlanner is a **planning, tracking and estimation tool** for cryptocurrency and other assets. It lets you manually record transactions, track a portfolio, view market data, plan entry and exit strategies, and generate **estimates** of your Australian capital gains tax (CGT) position.

**The Service is not financial, investment, tax, legal or accounting advice.**

You acknowledge and agree that:

- All calculations, figures, projections and reports produced by the Service, including CGT estimates, portfolio values, profit/loss figures, and any planning outputs, are **estimates only** and are provided for general informational and planning purposes.
- Tax calculations use the **FIFO (first-in, first-out) method and simplified ATO CGT rules**, and convert values to Australian dollars at **the exchange rate for the date of each transaction**, using Reserve Bank of Australia daily rates with European Central Bank rates as a fallback. A date falling on a weekend or public holiday uses the previous business day. These calculations may not reflect your complete or actual tax position.
- The accuracy of any output depends entirely on the **accuracy and completeness of the information you enter**. We do not verify your data.
- Nothing in the Service should be relied upon as a recommendation to buy, sell or hold any asset.
- You should **consult a registered tax agent, accountant or licensed financial adviser** before making any financial decision or lodging any tax return.

We are not a registered tax agent, financial adviser, or financial product provider, and we do not provide any regulated financial service.

---

## 3. Eligibility

You must be **at least 18 years old** and legally able to enter into a binding contract to use the Service. By using the Service, you confirm that you meet these requirements.

---

## 4. Your account

- You must provide accurate information when creating an account and keep it up to date.
- You are responsible for keeping your login credentials secure and for all activity that occurs under your account.
- You must notify us promptly at mycryptoplanner@outlook.com if you believe your account has been accessed without authorisation.
- You may delete your account at any time using the in-app account deletion feature, which permanently removes your account and associated data and cancels any active subscription (see section 6).

---

## 5. Free and Pro plans

The Service offers a **Free plan** and a paid **Pro plan**.

- The Free plan provides limited features and usage limits, as described on our pricing page.
- The Pro plan unlocks additional features for a recurring subscription fee, as displayed at the time of purchase (currently **A$15 per month or A$144 per year**, or such other price as we may display).
- We may change plan features, limits and pricing from time to time. Any price change will not affect your current billing period; we will give reasonable notice of changes that affect you.

All prices are in Australian dollars (AUD) unless stated otherwise and are inclusive of any applicable taxes unless stated otherwise.

---

## 6. Payments, billing, cancellation and refunds

**Payment processing.** Payments are processed securely by our third-party payment provider, **Stripe**. We do not store your full card details. Your use of Stripe is subject to Stripe's own terms and privacy policy.

**Billing.** Pro subscriptions renew automatically at the end of each billing period until cancelled. By subscribing, you authorise us (via Stripe) to charge the applicable fee to your payment method on a recurring basis.

**Cancellation.** You may cancel your Pro subscription at any time through the account/subscription settings in the app (via the Stripe customer portal). When you cancel:

- You will not be charged for any future billing period.
- Your Pro access continues until the end of the current paid billing period, after which your account reverts to the Free plan.

**Refunds.** Except where required by law, subscription fees are **non-refundable**, including for partial billing periods and unused time. We do not provide refunds simply because you changed your mind or did not use the Service.

**Your Australian Consumer Law rights.** Nothing in these Terms excludes, restricts or modifies any right or remedy you may have under the **Australian Consumer Law (ACL)** or other laws that cannot be lawfully excluded. Our services come with guarantees that cannot be excluded under the ACL. If we fail to meet a consumer guarantee, you may be entitled to a remedy (such as a refund) as required by law. If you believe you are entitled to a remedy, contact us at mycryptoplanner@outlook.com.

---

## 7. Acceptable use

You agree not to:

- Use the Service for any unlawful purpose or in breach of these Terms.
- Attempt to gain unauthorised access to the Service, other users' accounts, or our systems.
- Interfere with, disrupt, overload, or attempt to reverse engineer, scrape, or copy the Service or its underlying data or code.
- Resell, sublicense, or redistribute the Service or any data obtained through it.
- Use the Service to infringe the rights of any third party.

We may suspend or terminate your access if you breach these Terms or use the Service in a way that harms us, other users, or third parties.

---

## 8. Third-party data and services

The Service relies on third-party data providers and services, including (without limitation) **CoinGecko** (crypto market data), stock/ETF market data providers, **Supabase** (authentication and data storage), **Stripe** (payments), and **Vercel** (hosting).

- Market data, prices, and other third-party information are provided "as is" and may be **delayed, incomplete, or inaccurate**. We do not control and are not responsible for third-party data or services.
- Where required by our data providers, we display appropriate attribution (for example, "Data provided by CoinGecko").
- Your use of the Service may be affected by the availability of these third parties.

---

## 9. Your data and privacy

Our collection and handling of your personal information is described in our **Privacy Policy**, which forms part of these Terms. By using the Service, you consent to that handling.

You retain ownership of the data you enter. You can export your data and delete your account at any time using the in-app tools.

---

## 10. Intellectual property

The Service, including its software, design, branding, text, and content (excluding your own data and third-party data), is owned by us or our licensors and is protected by intellectual property laws. We grant you a limited, non-exclusive, non-transferable, revocable licence to use the Service for its intended purpose. You may not copy, modify, distribute, or create derivative works from the Service except as permitted by law.

---

## 11. Disclaimers

To the maximum extent permitted by law, and subject to section 6 (your ACL rights):

- The Service is provided **"as is" and "as available"**, without warranties of any kind, whether express or implied.
- We do not warrant that the Service will be uninterrupted, error-free, secure, or that any data, calculation, or output will be accurate, complete, or fit for a particular purpose.
- We are not responsible for any decision you make, or any tax return you lodge, based on the Service's outputs.

---

## 12. Limitation of liability

To the maximum extent permitted by law, and subject to section 6 (your ACL rights):

- We are not liable for any indirect, incidental, special, consequential, or punitive loss or damage, or for any loss of profits, revenue, data, or opportunity, arising out of or in connection with your use of (or inability to use) the Service.
- Our total aggregate liability to you for all claims arising out of or relating to the Service is limited to the greater of (a) the total fees you paid us in the **three (3) months** immediately before the event giving rise to the claim, or (b) AUD $100.
- Where our liability cannot be excluded under the ACL but can be limited, our liability is limited (at our option) to resupplying the service or paying the cost of having the service resupplied.

---

## 13. Indemnity

To the extent permitted by law, you agree to indemnify us against any loss, liability, or cost we reasonably incur arising from your breach of these Terms, your misuse of the Service, or your breach of any law or third-party right.

---

## 14. Suspension and termination

We may suspend or terminate your access to the Service (or any part of it) at any time if you breach these Terms, if required by law, or if we discontinue the Service. You may stop using the Service and delete your account at any time. Sections that by their nature should survive termination (including sections 2, 10, 11, 12, 13 and 15) will survive.

---

## 15. Changes to these Terms and the Service

We may update these Terms from time to time. If we make material changes, we will take reasonable steps to notify you (for example, by email or an in-app notice). Your continued use of the Service after changes take effect constitutes acceptance of the updated Terms. We may also modify, suspend, or discontinue features of the Service at any time.

---

## 16. Governing law

These Terms are governed by the laws of **Western Australia, Australia**. You and we submit to the non-exclusive jurisdiction of the courts of Western Australia and the courts competent to hear appeals from them. This does not affect any mandatory consumer protections available to you under the law of your country of residence.

---

## 17. Contact

If you have any questions about these Terms, contact us at:

**MyCryptoPlanner**
Trading as My Crypto Planner (ABN 46 604 154 915)
Email: **mycryptoplanner@outlook.com**

---

*These Terms of Service are a general-purpose draft and do not constitute legal advice. You should have them reviewed by a qualified Australian legal professional before relying on them.*
`;

const PRIVACY_MD = `# Privacy Policy

**Last updated: 1 September 2026**

This Privacy Policy explains how MyCryptoPlanner collects, uses, stores, and protects your personal information, and the rights you have in relation to it. By using the Service, you agree to the practices described in this policy.

We are committed to handling your personal information in accordance with the **Australian Privacy Principles (APPs)** under the *Privacy Act 1988* (Cth), and to respecting the privacy rights of users located elsewhere.

---

## 1. Who we are

MyCryptoPlanner (the "**Service**", "**we**", "**us**", "**our**") is operated by **Oliver Joseph Lazare**, a sole trader trading as **My Crypto Planner** (**ABN 46 604 154 915**), based in Western Australia, Australia.

For any privacy-related question or request, contact us at **mycryptoplanner@outlook.com**.

---

## 2. Our privacy-first approach

MyCryptoPlanner is designed to minimise the personal information we collect:

- We **do not** ask for or store your cryptocurrency exchange API keys.
- We **do not** connect to your exchange accounts or wallets.
- We **do not** store your payment card details (these are handled by our payment processor, Stripe).

You control what information you enter, and you can export or permanently delete your data at any time (see section 8).

---

## 3. Information we collect

**a) Information you provide directly**

- **Account information:** your email address and a password (stored securely in hashed form by our authentication provider).
- **Portfolio and transaction data:** the transactions, holdings, wallet labels, plans, tax estimates, and other information you manually enter into the Service.
- **Preferences:** settings such as your display currency, theme, and income bracket used for tax estimates.
- **Communications:** information you provide when you contact us (for example, by email).

**b) Payment information**

- When you subscribe to a paid plan, payment is processed by **Stripe**. Stripe collects and processes your payment details directly. We receive limited information from Stripe, such as your subscription status and a customer identifier. We do **not** receive your full card number.

**c) Information collected automatically**

- Basic technical information necessary to operate and secure the Service, such as log data and general usage information. We aim to keep this to a minimum.

We do **not** knowingly collect information from anyone under 18 years of age.

---

## 4. How we use your information

We use your personal information to:

- Provide, operate, and maintain the Service, including storing and syncing your portfolio data across your devices.
- Generate the estimates, calculations, reports, and planning outputs you request.
- Process your subscription and payments (via Stripe).
- Authenticate your account and keep it secure.
- Respond to your enquiries and provide support.
- Send you essential service communications (for example, account confirmation, password resets, and important updates).
- Comply with our legal obligations and enforce our Terms of Service.

We do **not** sell your personal information to anyone.

---

## 5. Who we share your information with

We share personal information only as necessary to run the Service, with the following categories of third parties ("**service providers**"):

- **Supabase**, authentication and secure cloud storage of your account and portfolio data.
- **Stripe**, payment processing and subscription management.
- **Vercel**, hosting and delivery of the Service.
- **Our email provider**, to send account and service emails.

These providers are bound by their own privacy and security obligations and may only use your information to provide services to us.

We may also disclose your information if required to do so by law, to protect our legal rights, or to prevent harm.

The Service also retrieves **market data** from providers such as **CoinGecko** and stock/ETF data providers. This is general market information and does not involve sharing your personal portfolio data with them.

---

## 6. Overseas storage and processing

Some of our service providers (including Supabase, Stripe, and Vercel) may store or process data on servers located **outside Australia**. Where this occurs, we take reasonable steps to ensure your information is handled in accordance with this policy and applicable privacy laws. By using the Service, you consent to your information being stored and processed overseas by these providers.

---

## 7. How we protect your information

We take reasonable technical and organisational measures to protect your personal information, including:

- Encrypted connections (HTTPS) between your device and the Service.
- Secure authentication and access controls, with data access restricted to your own account.
- Reliance on reputable, security-conscious infrastructure providers.

No method of transmission or storage is completely secure, and we cannot guarantee absolute security. You are responsible for keeping your account credentials confidential.

---

## 8. Your rights and choices

You can:

- **Access and update** most of your information directly within the app.
- **Export your data** using the in-app export tools.
- **Delete your account and data** at any time using the in-app account deletion feature, which permanently removes your account and associated data from our systems and cancels any active subscription. Some limited information may be retained by our payment processor (Stripe) where required for financial or legal record-keeping.
- **Change your preferences**, including opting out of non-essential communications.

Depending on where you live, you may have additional rights under local law (for example, under the EU/UK GDPR, rights to access, rectification, erasure, restriction, portability, and objection). To exercise any of these rights, contact us at **mycryptoplanner@outlook.com**. We will respond within a reasonable time and in accordance with applicable law.

---

## 9. Data retention

We retain your personal information for as long as your account is active or as needed to provide the Service. When you delete your account, we delete your associated data, except where we are required to retain certain records (for example, transaction records held by Stripe) to comply with legal, tax, or accounting obligations.

---

## 10. Cookies and local storage

The Service uses cookies and browser local storage that are necessary for it to function, for example to keep you logged in and to remember your preferences. We do not use these for third-party advertising. You can control cookies through your browser settings, but disabling them may affect how the Service works.

---

## 11. Third-party links

The Service may contain links to third-party websites (for example, an asset's official website). We are not responsible for the privacy practices or content of those sites. We encourage you to review their privacy policies.

---

## 12. Changes to this policy

We may update this Privacy Policy from time to time. If we make material changes, we will take reasonable steps to notify you (for example, by email or an in-app notice) and update the "last updated" date above. Your continued use of the Service after changes take effect constitutes acceptance of the updated policy.

---

## 13. Complaints and contact

If you have a question, request, or complaint about how we handle your personal information, please contact us first at:

**MyCryptoPlanner**
Trading as My Crypto Planner (ABN 46 604 154 915)
Email: **mycryptoplanner@outlook.com**

We will investigate and respond to your complaint within a reasonable time. If you are located in Australia and are not satisfied with our response, you may contact the **Office of the Australian Information Commissioner (OAIC)** at [www.oaic.gov.au](https://www.oaic.gov.au). Users in other jurisdictions may have the right to lodge a complaint with their local data protection authority.

---

*This Privacy Policy is a general-purpose draft and does not constitute legal advice. You should have it reviewed by a qualified Australian legal professional before relying on it.*
`;

const DISCLAIMER_MD = `# Disclaimer

**Last updated: 1 September 2026**

Please read this Disclaimer carefully. It applies to your use of MyCryptoPlanner (the "**Service**") and forms part of our [Terms of Service](/terms). By using the Service, you acknowledge and accept the matters set out below.

The Service is operated by **Oliver Joseph Lazare**, a sole trader trading as **My Crypto Planner** (**ABN 46 604 154 915**), based in Western Australia, Australia. You can contact us at **mycryptoplanner@outlook.com**.

---

## 1. Not financial, investment, tax, legal or accounting advice

MyCryptoPlanner is a **planning, tracking and estimation tool only**. Nothing in the Service constitutes financial, investment, tax, legal, or accounting advice, and nothing should be relied upon as such.

- We are **not** a registered tax agent, accountant, financial adviser, or licensed financial services provider.
- The Service does **not** take into account your personal objectives, financial situation, or needs.
- Nothing in the Service is a recommendation to buy, sell, hold, or otherwise deal in any cryptocurrency, security, or other asset.

You should obtain advice from a **registered tax agent, accountant, or licensed financial adviser** before making any financial decision or lodging any tax return.

---

## 2. Estimates only

All figures, calculations, projections, and reports produced by the Service, including capital gains tax (CGT) estimates, portfolio values, profit and loss figures, break-even figures, and any planning or "what-if" outputs, are **estimates for general informational and planning purposes only**. They are **not** a definitive statement of your financial or tax position.

In particular, you acknowledge that:

- Tax estimates are calculated using the **FIFO (first-in, first-out) method and simplified ATO capital gains tax rules**, which may differ from the method or rules that apply to your specific circumstances.
- Values are converted to Australian dollars using **the exchange rate applicable on the date of each individual transaction**, sourced from Reserve Bank of Australia daily rates with European Central Bank rates as a fallback. A date falling on a weekend or public holiday uses the previous business day. Where no published rate can be found for a date, the current rate is used instead and those figures are approximate.
- The estimates **do not** account for your complete tax position, other income, deductions, offsets, or individual circumstances.
- Actual capital gains tax payable depends on your chosen accounting method, your full financial position, and current tax law, and should be confirmed with a registered tax agent.

---

## 3. Accuracy depends on your data

The Service works from information that **you enter manually**. We do not connect to your exchanges or wallets, and we do not verify the accuracy or completeness of the data you provide.

The accuracy of any output therefore depends entirely on the accuracy and completeness of the information you enter. Missing, incorrect, or incomplete transactions will produce inaccurate results.

---

## 4. Market data and third-party information

The Service displays market data and other information obtained from third-party providers, including **CoinGecko** and stock/ETF data providers.

- This data is provided "**as is**" and may be **delayed, incomplete, inaccurate, or unavailable**.
- We do not control, verify, or guarantee third-party data, and we are not responsible for any loss arising from reliance on it.
- Prices and values shown are indicative only and may not match the price at which any asset can actually be bought or sold.

---

## 5. Cryptocurrency and investment risk

Cryptocurrency and other asset markets are **highly volatile and speculative**. Prices can rise or fall significantly and rapidly. Past performance is not an indicator of future performance. You may lose some or all of the value of your holdings. You are solely responsible for your own investment and financial decisions.

---

## 6. No guarantee of results or availability

We do not warrant that the Service will be uninterrupted, error-free, or continuously available, or that any output will be accurate, complete, or fit for a particular purpose. We may modify, suspend, or discontinue any part of the Service at any time.

---

## 7. Your responsibility

You use the Service, and rely on its outputs, **at your own risk**. To the maximum extent permitted by law, we are not liable for any decision you make, or any tax return you lodge, based on information or estimates provided by the Service.

Nothing in this Disclaimer excludes, restricts, or modifies any consumer guarantee, right, or remedy you may have under the **Australian Consumer Law** or other laws that cannot lawfully be excluded. See our [Terms of Service](/terms) for more detail on your rights and our liability.

---

## 8. Contact

If you have any questions about this Disclaimer, contact us at:

**MyCryptoPlanner**
Trading as My Crypto Planner (ABN 46 604 154 915)
Email: **mycryptoplanner@outlook.com**

---

*This Disclaimer is a general-purpose draft and does not constitute legal advice. You should have it reviewed by a qualified Australian legal professional before relying on it.*
`;

export const LEGAL_DOCS: Record<LegalDocId, LegalDoc> = {
  terms:      { title: 'Terms of Service', content: TERMS_MD },
  privacy:    { title: 'Privacy Policy',   content: PRIVACY_MD },
  disclaimer: { title: 'Disclaimer',       content: DISCLAIMER_MD },
};
